const COMPONENT_NAME = 'header-section';

if (!customElements.get(COMPONENT_NAME)) {
  class HeaderSection extends HTMLElement {
    constructor() {
      super();
      this.MQ_DESKTOP = window.matchMedia('(min-width: 1024px)');
      this.TOUCH = window.matchMedia('(hover: none)').matches;

      this.openItem = null;
      this.closeTimer = null;
      this.drawerEl = null;
      this.toggleEl = null;
      this.sectionWrapper = null;

      this.handleDocClick = this.handleDocClick.bind(this);
      this.handleDocKey = this.handleDocKey.bind(this);
      this.handleDocFocusIn = this.handleDocFocusIn.bind(this);
      this.handleBreakpoint = this.handleBreakpoint.bind(this);
      this.positionDrawer = this.positionDrawer.bind(this);
      this.updateMegaMaxHeight = this.updateMegaMaxHeight.bind(this);
      this.refreshCount = this.refreshCount.bind(this);
    }

    connectedCallback() {
      this.sectionWrapper = this.closest('.section-header') || this;
      this.initSticky();
      this.initMegamenu();
      this.initTabs();
      this.initMobileDrawer();
      this.initCart();

      document.addEventListener('click', this.handleDocClick);
      document.addEventListener('keydown', this.handleDocKey);
      document.addEventListener('focusin', this.handleDocFocusIn);
      document.addEventListener('theme:cartchanged', this.refreshCount);
      this.MQ_DESKTOP.addEventListener('change', this.handleBreakpoint);

      this.updateMegaMaxHeight();
      window.addEventListener('scroll', this.updateMegaMaxHeight, { passive: true });
      window.addEventListener('resize', this.updateMegaMaxHeight);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocClick);
      document.removeEventListener('keydown', this.handleDocKey);
      document.removeEventListener('focusin', this.handleDocFocusIn);
      document.removeEventListener('theme:cartchanged', this.refreshCount);
      this.MQ_DESKTOP.removeEventListener('change', this.handleBreakpoint);
      window.removeEventListener('scroll', this.updateMegaMaxHeight);
      window.removeEventListener('resize', this.updateMegaMaxHeight);
      if (this.stickyObserver) this.stickyObserver.disconnect();
    }

    initSticky() {
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;top:0;left:0;height:1px;width:1px;';
      this.parentNode.insertBefore(sentinel, this);
      this.stickyObserver = new IntersectionObserver(
        ([entry]) => this.classList.toggle('is-stuck', !entry.isIntersecting),
        { threshold: [1] }
      );
      this.stickyObserver.observe(sentinel);
    }

    isDesktop() { return this.MQ_DESKTOP.matches; }

    initMegamenu() {
      const items = this.querySelectorAll('[data-mega-item]');

      items.forEach((item) => {
        const trigger = item.querySelector('[data-mega-trigger]');
        if (!trigger) return;

        item.addEventListener('mouseenter', () => {
          if (!this.isDesktop() || this.TOUCH) return;
          clearTimeout(this.closeTimer);
          this.openMega(item);
        });
        item.addEventListener('mouseleave', () => {
          if (!this.isDesktop() || this.TOUCH) return;
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => this.closeMega(item), 120);
        });

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const expanded = trigger.getAttribute('aria-expanded') === 'true';
          if (expanded) this.closeMega(item);
          else this.openMega(item);
        });

        trigger.addEventListener('focus', () => {
          if (!this.isDesktop()) return;
          this.openMega(item);
        });
      });
    }

    openMega(item) {
      if (this.openItem && this.openItem !== item) this.closeMega(this.openItem);
      const trigger = item.querySelector('[data-mega-trigger]');
      const panel = item.querySelector('[data-mega-panel]');
      if (!trigger || !panel) return;
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.openItem = item;
    }

    closeMega(item) {
      const trigger = item.querySelector('[data-mega-trigger]');
      const panel = item.querySelector('[data-mega-panel]');
      if (!trigger || !panel) return;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;
    }

    initTabs() {
      this.querySelectorAll('.mega--tabbed').forEach((mega) => {
        const tabs = Array.from(mega.querySelectorAll('[data-mega-tab]'));
        const panes = Array.from(mega.querySelectorAll('[data-mega-pane]'));
        if (!tabs.length) return;

        const activate = (idx) => {
          tabs.forEach((t, i) => {
            const active = i === idx;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
            t.setAttribute('tabindex', active ? '0' : '-1');
          });
          panes.forEach((p, i) => {
            const active = i === idx;
            p.classList.toggle('is-active', active);
            if (active) p.removeAttribute('hidden');
            else p.setAttribute('hidden', '');
          });
        };

        tabs.forEach((tab, i) => {
          tab.addEventListener('mouseenter', () => activate(i));
          tab.addEventListener('focus', () => activate(i));
          tab.addEventListener('click', () => activate(i));
          tab.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              e.preventDefault();
              const dir = e.key === 'ArrowRight' ? 1 : -1;
              const next = (i + dir + tabs.length) % tabs.length;
              tabs[next].focus(); activate(next);
            } else if (e.key === 'Home') { e.preventDefault(); tabs[0].focus(); activate(0); }
            else if (e.key === 'End')   { e.preventDefault(); tabs[tabs.length - 1].focus(); activate(tabs.length - 1); }
          });
        });
      });
    }

    initMobileDrawer() {
      this.drawerEl = this.querySelector('[data-mobile-drawer]');
      this.toggleEl = this.querySelector('[data-mobile-toggle]');
      if (!this.drawerEl || !this.toggleEl) return;

      this.toggleEl.addEventListener('click', () => {
        const expanded = this.toggleEl.getAttribute('aria-expanded') === 'true';
        if (expanded) this.closeDrawer(); else this.openDrawer();
      });
    }

    updateMegaMaxHeight() {
      const rect = this.sectionWrapper.getBoundingClientRect();
      const available = Math.max(0, window.innerHeight - Math.max(0, rect.bottom));
      this.style.setProperty('--mega-max-h', available + 'px');
    }

    positionDrawer() {
      if (!this.drawerEl) return;
      const rect = this.sectionWrapper.getBoundingClientRect();
      this.drawerEl.style.top = Math.max(0, rect.bottom) + 'px';
    }

    openDrawer() {
      this.drawerEl.hidden = false;
      this.positionDrawer();
      void this.drawerEl.offsetWidth;
      this.drawerEl.classList.add('is-open');
      this.toggleEl.setAttribute('aria-expanded', 'true');
      window.addEventListener('scroll', this.positionDrawer, { passive: true });
      window.addEventListener('resize', this.positionDrawer);
      document.body.classList.add('drawer-open');

      const firstItem = this.drawerEl.querySelector('[data-mega-item]');
      if (firstItem) setTimeout(() => this.openMega(firstItem), 0);
    }

    closeDrawer() {
      if (!this.drawerEl) return;
      this.drawerEl.classList.remove('is-open');
      this.toggleEl.setAttribute('aria-expanded', 'false');
      window.removeEventListener('scroll', this.positionDrawer);
      window.removeEventListener('resize', this.positionDrawer);
      document.body.classList.remove('drawer-open');
      setTimeout(() => { this.drawerEl.hidden = true; }, 250);
    }

    initCart() {
      this.querySelectorAll('[data-cart-trigger]').forEach((link) => {
        link.addEventListener('click', (e) => {
          if (!document.querySelector('.cart-drawer')) return;
          e.preventDefault();
          document.dispatchEvent(
            new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false })
          );
        });
      });
    }

    refreshCount() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          this.querySelectorAll('[data-cart-count]').forEach((el) => {
            el.textContent = cart.item_count;
            el.classList.toggle('is-hidden', cart.item_count === 0);
          });
        })
        .catch(() => {});
    }

    handleDocClick(e) {
      if (!this.openItem) return;
      if (!this.isDesktop()) return;
      if (!this.openItem.contains(e.target)) this.closeMega(this.openItem);
    }

    handleDocKey(e) {
      if (e.key === 'Escape') {
        if (this.openItem) {
          const trigger = this.openItem.querySelector('[data-mega-trigger]');
          this.closeMega(this.openItem);
          if (trigger) trigger.focus();
        }
        if (this.drawerEl && this.drawerEl.classList.contains('is-open')) {
          this.closeDrawer();
          if (this.toggleEl) this.toggleEl.focus();
        }
      }
    }

    handleDocFocusIn(e) {
      if (!this.openItem) return;
      if (!this.isDesktop()) return;
      if (this.openItem.contains(e.target)) return;
      this.closeMega(this.openItem);
    }

    handleBreakpoint(e) {
      if (this.openItem) this.closeMega(this.openItem);
      if (e.matches && this.drawerEl && this.drawerEl.classList.contains('is-open')) {
        this.closeDrawer();
      }
    }
  }

  customElements.define(COMPONENT_NAME, HeaderSection);
}
